import { useCouponsContext } from "../components/CouponsProvider"
import { useSelectorTyped } from "../store/configureStore"
import { useRegion } from "./useRegion"
import { useStudentMinutes } from "./useStudentMinutes"

export function useLessonCouponMatch(address: string | null) {
  const { auth } = useSelectorTyped((state) => state.firebase)
  const coupons = useCouponsContext()
  const studentMinutes = useStudentMinutes(auth.uid)
  const regionHook = useRegion(address)
  const isFirstPurchase =
    (studentMinutes?.drivingMinutesPurchased ?? 0) === 0 &&
    (studentMinutes?.observationMinutesPurchased ?? 0) === 0
  const emptyResult = { coupon: undefined, regionId: undefined }

  if (!coupons.lessonCoupons || !coupons.roadTestCoupons) {
    return emptyResult
  }

  const lessonCoupons = Object.values(coupons.lessonCoupons)
  const roadTestCoupons = Object.values(coupons.roadTestCoupons)
  const allCoupons = [...lessonCoupons, ...roadTestCoupons]

  const regionId = regionHook.data?.id
  let maxDiscountCoupon:
    | {
        code: string
        discount: number
      }
    | undefined

  allCoupons
    .filter((coupon) => {
      if (!coupon.displayInLandingPages) {
        return false
      }

      if (coupon.expiration && coupon.expiration < Date.now()) {
        return false
      }

      if (coupon.regions.length > 0) {
        if (!regionId) {
          return false
        }

        return coupon.regions.some((region) => region.id === regionId)
      }

      return true
    })
    .forEach((coupon) => {
      if (coupon.discount > (maxDiscountCoupon?.discount ?? 0)) {
        maxDiscountCoupon = { code: coupon.code, discount: coupon.discount }
      }
    })

  if (!maxDiscountCoupon) {
    return emptyResult
  }

  const coupon =
    coupons.lessonCoupons[maxDiscountCoupon.code] ??
    coupons.roadTestCoupons[maxDiscountCoupon.code]

  if (coupon.onlyFirstPurchase && !isFirstPurchase) {
    return emptyResult
  }

  return {
    coupon,
    regionId: coupon.regions.find((region) => region.id === regionId)?.id,
  }
}
